import { NgTemplateOutlet } from '@angular/common';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';
import { FormsModule } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  model,
  OnInit,
  signal,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { GroupService } from '../group.service';
import {
  EditIconComponent,
  ChevronDownIconComponent,
  SearchIconComponent,
} from '@kuiper/icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { GroupDetails } from '../group-model';
import { UserData } from '../../users-database/user-model';
import { BaseCardList } from '../../base/base-card-list';
import { Subscription } from 'rxjs';
import { AvatarPipe } from '../../../../../../ui-components/pipes/avatar/avatar.pipe';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { GroupUserCardComponent } from '../../../../../common/components/group-user-card/group-user-card.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { LoaderComponent } from '../../../../../../ui-components/lib/loader/loader.component';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { ApiResponse } from '../../../../../common/models/api-response.model';
import { Option } from '../../../../../common/models/option.model';

@Component({
  selector: 'lib-group-details',
  imports: [
    NgTemplateOutlet,
    AvatarPipe,
    OverlayLoaderComponent,
    RouterLink,
    GroupUserCardComponent,
    FormsModule,
    TranslocoPipe,
    LoaderComponent,
    PaginatorComponent,
    SearchIconComponent,
    ChevronDownIconComponent,
    EditIconComponent,
    TenantPathPipe,
  ],
  providers: [provideSystemTabsLayout()],
  templateUrl: './group-details.component.html',
  styleUrl: './group-details.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GroupDetailsComponent
  extends BaseCardList
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly ADMIN_ROUTES = ADMIN_ROUTES;
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();

  route = inject(ActivatedRoute);
  groupService = inject(GroupService);
  router = inject(Router);
  tenantService = inject(TenantService);

  subscriptions = new Subscription();
  isLoading = signal<boolean>(false);
  groupDetails = model.required<GroupDetails>();
  groupId = signal<string>('');

  ngOnInit(): void {
    this.subscriptions.add(
      this.route.params.subscribe(params => {
        this.groupId.set(params['id']);
        this.getGroupById();
        this.getCardList();
      })
    );
  }

  ngAfterViewInit(): void {
    this.searchTermWrapper();
  }

  getGroupById() {
    this.isLoading.set(true);
    this.subscriptions.add(
      this.groupService.getGroup(this.groupId()).subscribe({
        next: (response: { data: GroupDetails }) => {
          this.groupDetails.set(response.data);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.router.navigate([
            this.tenantService.adminPath(`${ADMIN_ROUTES.GROUPS.LIST}`),
          ]);
        },
      })
    );
  }

  getCardList(searchName = '') {
    this.isCardListLoading.set(true);
    this.subscriptions.add(
      this.groupService
        .getGroupUsers(
          this.groupId(),
          this.cardListCurrentPage(),
          15,
          searchName
        )
        .subscribe({
          next: (response: ApiResponse<UserData>) => {
            const users = this.mapAssignedUsers(response.data);
            this.handleCardListResponse(users, response.meta);
          },
        })
    );
  }

  mapAssignedUsers(users: UserData[]): Option[] {
    return users
      .map((user: UserData) => {
        return {
          label: user.name,
          value: user.id.toString(),
          image: user.image,
          job_title: user.placement?.job_title?.name || '',
        };
      })
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
  }

  goBack() {
    history.back();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
